import { memo } from "react";
import { SmoothLink as Link } from "@/components/navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { BookOpen, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BlogPost } from "@/hooks/useBlogPosts";
import { getOptimizedImageUrl } from "@/lib/image-utils";

// Truncate alt text to 100 characters max for SEO compliance
const truncateAlt = (text: string, maxLength = 100): string => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
};

interface RelatedArticlesSectionProps {
  articles: BlogPost[];
  treatmentCategory?: string;
}

export const RelatedArticlesSection = memo(function RelatedArticlesSection({ 
  articles,
  treatmentCategory 
}: RelatedArticlesSectionProps) {
  if (!articles || articles.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-lg bg-accent/10 flex-shrink-0">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
            </div>
            <div className="min-w-0">
              <CardTitle as="h2" className="text-base sm:text-xl truncate">Related Articles</CardTitle>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 hidden sm:block">
                Expert insights and patient guides
              </p>
            </div>
          </div>
          <Link to="/blog/" className="flex-shrink-0">
            <Button variant="ghost" size="sm" className="gap-1 text-xs sm:text-sm h-7 sm:h-8 px-2">
              <span className="hidden xs:inline">View All</span>
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pb-4 sm:pb-6">
        <div className="grid gap-3">
          {articles.map((article) => (
            <Link 
              key={article.id} 
              to={`/blog/${article.slug}/`}
              className="group"
            >
              <div className="flex gap-3 p-2 sm:p-3 rounded-lg border border-border/50 hover:border-primary/30 hover:bg-muted/30 transition-all">
                {/* Thumbnail */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden shrink-0">
                  <AspectRatio ratio={1}>
                    {article.hero_image_url || article.image_url ? (
                      <img
                        src={getOptimizedImageUrl(article.hero_image_url || article.image_url, 200, 200)}
                        alt={truncateAlt(article.title)}
                        width={200}
                        height={200}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-primary/50" />
                      </div>
                    )}
                  </AspectRatio>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                    {article.category && (
                      <Badge variant="secondary" className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0">
                        {article.category}
                      </Badge>
                    )}
                    {article.read_time_minutes && (
                      <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-0.5 sm:gap-1">
                        <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                        {article.read_time_minutes}m
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium text-xs sm:text-sm line-clamp-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  {article.excerpt && (
                    <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1 mt-0.5 sm:mt-1 hidden sm:block">
                      {article.excerpt}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});
